/**
 * Цветовые статусы ячеек шахматки номеров (manager v3 §4.1 / §4.4).
 *
 * Дизайн-палитра (manager v3):
 *   свободно            — cream-input (светлый бежевый), бордюр
 *   занят               — warm-charcoal (тёмно-коричневый) + champagne текст
 *   hold / уборка       — beige (#e8d6b8) + коричневый текст
 *   грязный / OOO       — soft-red (#f1d2d2) + бордовый текст
 *
 * Эти токены НЕ совпадают с тоновыми бейджами списка (.fleet-badge--*),
 * у шахматки своя палитра по макету — поэтому отдельные CSS-классы
 * `.cb-cell--<tone>` (см. global.css).
 */
import type { RoomStatus } from '@hsc/types';

/** Визуальный тон ячейки шахматки. */
export type CellTone = 'free' | 'busy' | 'hold' | 'dirty';

/**
 * Статус занятости ячейки. `free` — пусто; остальные — по проживанию
 * или по статусу самого номера (OOO / уборка вне брони).
 */
export type CellKind =
  | { tone: 'free' }
  | { tone: 'busy'; label: string; stayId: string; guestId?: string }
  | { tone: 'hold'; label: string; stayId: string; guestId?: string }
  | { tone: 'dirty'; label: string };

/**
 * Сырой статус проживания из occupancy (stay.domain): booked | checked_in |
 * checked_out | no_show | cancelled. Cancelled/no_show отфильтрованы бэком,
 * но мапу держим полной — никаких сырых англ-кодов в UI.
 */
const OCCUPANCY_LABEL: Readonly<Record<string, string>> = {
  booked: 'Бронь',
  confirmed: 'Бронь',
  pending: 'Бронь',
  checked_in: 'Заехал',
  checked_out: 'Выехал',
  no_show: 'Не заехал',
  cancelled: 'Отменено',
};

/** Русский label статуса занятости (без сырых англ-кодов). */
export function occupancyLabel(status: string): string {
  return OCCUPANCY_LABEL[status] ?? status;
}

/**
 * Тон ячейки по статусу проживания: подтверждённые/заехавшие — «занят»
 * (charcoal), ещё не подтверждённые брони — «hold» (beige).
 */
export function occupancyTone(status: string): 'busy' | 'hold' {
  return status === 'booked' || status === 'pending' ? 'hold' : 'busy';
}

/**
 * Тон ячейки по статусу самого номера, когда брони на день нет:
 * cleaning → грязный/уборка, maintenance/out_of_order → грязный (peach),
 * иначе свободно.
 */
export function roomBaseTone(status: RoomStatus): CellTone {
  switch (status) {
    case 'pending_inspection':
    case 'cleaning':
    case 'repair':
    case 'maintenance':
    case 'out_of_order':
    case 'seasonal_closed':
      return 'dirty';
    default:
      return 'free';
  }
}

/** Русский label фонового статуса номера (для пустых ячеек/легенды). */
const ROOM_BASE_LABEL: Readonly<Record<RoomStatus, string>> = {
  available: 'Свободен',
  occupied: 'Занят',
  pending_inspection: 'На проверке',
  cleaning: 'На уборке',
  repair: 'Ремонт',
  maintenance: 'Ремонт',
  out_of_order: 'Закрыт/OOO',
  seasonal_closed: 'Закрыт/OOO',
};

export function roomBaseLabel(status: RoomStatus): string {
  return ROOM_BASE_LABEL[status];
}
